import DocumentService from '../services/document.service'
import { buildAppUrl } from './appOrigin'

const projectDocumentCache = new Map<string, { fileName: string; url: string }>()

function getCacheKey(projectId: string, documentId: string | number) {
    return `${projectId}:${documentId}`
}

export function getProjectDocumentName(document: any) {
    return document?.name
        || document?.title
        || document?.filename
        || document?.file_name
        || document?.original_filename
        || `Document #${document?.id ?? ''}`
}

function getDownloadName(headers: any, fallbackName: string) {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition']
    const utfMatch = disposition?.match(/filename\*=UTF-8''([^;]+)/i)

    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1])
    }

    const plainMatch = disposition?.match(/filename="?([^"]+)"?/i)

    return plainMatch?.[1] || fallbackName
}

function triggerBrowserDownload(url: string, fileName: string) {
    const link = document.createElement('a')

    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
}

function openInNewWindow(url: string) {
    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer')
    openedWindow?.focus()
}

export async function ensureProjectDocumentFile(projectId: string, documentId: string | number, fallbackName: string) {
    const cacheKey = getCacheKey(projectId, documentId)
    const cachedFile = projectDocumentCache.get(cacheKey)

    if (cachedFile) {
        return cachedFile
    }

    const response = await DocumentService.download(projectId, documentId)
    const blobUrl = window.URL.createObjectURL(response.data)
    const fileName = getDownloadName(response.headers, fallbackName)
    const fileData = {
        fileName,
        url: blobUrl,
    }

    projectDocumentCache.set(cacheKey, fileData)

    return fileData
}

export async function openProjectDocument(
    projectId: string,
    documentData: { id: string | number; [key: string]: any },
    options?: { downloadOnFirstOpen?: boolean }
) {
    const cacheKey = getCacheKey(projectId, documentData.id)
    const cachedFile = projectDocumentCache.get(cacheKey)

    if (cachedFile) {
        openInNewWindow(cachedFile.url)
        return cachedFile
    }

    const fileData = await ensureProjectDocumentFile(projectId, documentData.id, getProjectDocumentName(documentData))

    if (options?.downloadOnFirstOpen !== false) {
        triggerBrowserDownload(fileData.url, fileData.fileName)
    }

    openInNewWindow(fileData.url)

    return fileData
}

export function buildProjectDocumentLink(projectId: string, documentId: string | number) {
    const url = buildAppUrl(`/projects/${projectId}`)
    url.searchParams.set('view', 'documents')
    url.searchParams.set('document', String(documentId))
    url.searchParams.set('action', 'open')
    return url.toString()
}
